import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { useStore } from './store/useStore';
import Login from './pages/Login';
import AyudaAcceso from './pages/AyudaAcceso';
import AppShell from './components/AppShell';
import LimiteDeError from './components/LimiteDeError';
import MailApp from './pages/MailApp';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Drive from './pages/Drive';
import DriveEditor from './pages/DriveEditor';
import AppOffice from './pages/AppOffice';
import FormEditor from './pages/FormEditor';
import FormResponder from './pages/FormResponder';
import Casos from './pages/Casos';
import { BUZON_CASOS } from './lib/modulos';
import CalendarPage from './pages/CalendarPage';
import Reservar from './pages/publico/Reservar';
import Cita from './pages/publico/Cita';
import Rsvp from './pages/publico/Rsvp';
import { RutaFormularioPublico } from './components/forms/buzon/SeguimientoCaso';
import CarpetaCompartida from './pages/CarpetaCompartida';
import {
  codigoDeReunion, enDocs, enMeet, esRutaDeDocumento, esRutaPublicaDeDocs, meetActivo, puedeSaltarADocs, puedeVivirEnDocs,
  saltarADocs, urlEnMeet, volverAlCorreo,
} from './lib/dominios';
import './App.css';

// Orvian Meet (meet.orvian.mx) se carga aparte: livekit y sus pantallas no entran en el bundle del correo
const MeetApp = lazy(() => import(/* webpackChunkName: "meet" */ './meet/MeetApp'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 15000 } },
});

function AlCorreo() {
  useEffect(() => { volverAlCorreo(); }, []);
  return null;
}

/* /admin exige rol también aquí (A28): el backend ya lo exigía, pero un usuario normal que
   escribiera la dirección veía tablas vacías sin saber por qué. */
function SoloAdmin({ children }) {
  const user = useStore((s) => s.user);
  if (user?.role === 'admin' || user?.role === 'owner') return children;
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 p-10 text-center" data-testid="admin-sin-permiso">
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>No tienes permiso para ver esta página</div>
      <p style={{ fontSize: 14, color: 'var(--text-2)', maxWidth: 360 }}>
        La administración de Orvian es solo para administradores. Si crees que deberías tener acceso, pídeselo a quien administra el correo.
      </p>
    </div>
  );
}

function Protected({ children }) {
  const user = useStore((s) => s.user);
  const loc = useLocation();
  if (!user) {
    // En docs no se entra: se vuelve a la misma ruta en el correo, que guarda el destino, pide
    // entrar y, ya con sesión, devuelve a la persona a docs.
    if (enDocs()) return <AlCorreo />;
    // Guardamos el destino con su query: si venimos del clic en una notificación
    // (/mail?a=…&u=…) y la sesión caducó, aterrizar en la bandeja genérica sería
    // perder justo el correo que el usuario quería leer. Igual con un documento.
    try {
      if (loc.search || puedeVivirEnDocs(loc.pathname)) sessionStorage.setItem('zuno_next', loc.pathname + loc.search);
    } catch (_) {}
    return <Navigate to="/login" replace />;
  }
  return children;
}

/*
 * Cada ruta en su dominio (ver lib/dominios.js):
 *  · en docs, lo que no es un documento vuelve al correo ("Ver en Drive", el acceso, /mail…);
 *  · en el correo, un documento, hoja o presentación se va a docs, pero solo con sesión: sin
 *    ella primero se entra aquí (si no, docs y el correo se lo pasarían sin fin).
 * `/file/` y `/open/` dependen del archivo: los decide DriveEditor al cargarlo.
 */
function GuardaDominio({ children }) {
  const loc = useLocation();
  const user = useStore((s) => s.user);
  const saliendo = useRef(false);
  const alCorreo = enDocs() && !puedeVivirEnDocs(loc.pathname);
  // Un enlace de reunión (mail.orvian.mx/abc-defg-hij) o /meet abierto en el correo: a Orvian Meet
  const reunion = codigoDeReunion(loc.pathname);
  const aMeet = meetActivo() && (reunion || loc.pathname === '/meet') ? urlEnMeet(reunion ? `/${reunion}` : '/') : null;
  // Responder un formulario no pide sesión: esa ruta va a docs aunque no se haya entrado
  const aDocs = !enDocs() && puedeSaltarADocs()
    && ((!!user && esRutaDeDocumento(loc.pathname)) || esRutaPublicaDeDocs(loc.pathname));
  useEffect(() => {
    if (aMeet) {
      saliendo.current = true;
      window.location.replace(aMeet);
    } else if (alCorreo) {
      saliendo.current = true;
      volverAlCorreo();
    } else if (aDocs) {
      saliendo.current = saltarADocs();
    }
  }, [alCorreo, aDocs, aMeet, loc.key]);
  if (aMeet || alCorreo || aDocs || saliendo.current) return null;
  return children;
}

export default function App() {
  const theme = useStore((s) => s.theme);
  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [theme]);

  // meet.orvian.mx: solo Orvian Meet (inicio y reuniones), con su propio enrutador (meet/MeetApp.jsx)
  if (enMeet()) {
    return (
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<div className="zuno-app" style={{ minHeight: '100vh', background: 'var(--bg)' }} />}>
          <MeetApp />
        </Suspense>
        <Toaster position="bottom-center" richColors closeButton />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="zuno-app">
          <LimiteDeError completa>
          <GuardaDominio>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Qué hacer cuando la contraseña no entra. No es un «restablecer»: la contraseña es
                la del buzón del proveedor y se cambia allí (ver AyudaAcceso.jsx). */}
            <Route path="/ayuda-acceso" element={<AyudaAcceso />} />
            {/* Drive: enlace público y editores a pantalla completa (sin la barra del correo) */}
            <Route path="/open/:token" element={<DriveEditor mode="public" />} />
            {/* Carpeta compartida por enlace: sin sesión si el acceso es público (ver claude/drive.md) */}
            <Route path="/carpeta/:token" element={<CarpetaCompartida />} />
            {/* Orvian Calendar, páginas públicas sin sesión (siempre en el dominio del correo, ver claude/calendario.md):
                reservar una cita, gestionar la cita reservada y responder una invitación desde el correo */}
            <Route path="/reservar/:slug" element={<Reservar />} />
            <Route path="/cita/:token" element={<Cita />} />
            <Route path="/rsvp/:token" element={<Rsvp />} />
            {/* Orvian Forms: responder es público (si pide sesión lo dice la página); editar y la vista previa, no */}
            {/* …y con ?seguimiento=<t>, la página de seguimiento de un caso del buzón (SeguimientoCaso.jsx) */}
            <Route path="/f/:token" element={<RutaFormularioPublico />} />
            <Route path="/form/:id" element={<Protected><FormEditor /></Protected>} />
            <Route path="/form/:id/preview" element={<Protected><FormResponder modo="vista_previa" /></Protected>} />
            {/* Editor para la app del teléfono: sin Protected, la sesión la trae la app (ver AppOffice.jsx) */}
            <Route path="/app/office/:id" element={<AppOffice />} />
            <Route path="/document/:id" element={<Protected><DriveEditor /></Protected>} />
            <Route path="/spreadsheet/:id" element={<Protected><DriveEditor /></Protected>} />
            <Route path="/presentation/:id" element={<Protected><DriveEditor /></Protected>} />
            <Route path="/file/:id" element={<Protected><DriveEditor /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/mail" element={<MailApp />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin" element={<SoloAdmin><Admin /></SoloAdmin>} />
              <Route path="/drive" element={<Drive />} />
              <Route path="/casos" element={BUZON_CASOS ? <Casos /> : <Navigate to="/mail" replace />} />
              <Route path="/calendar" element={<CalendarPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/mail" replace />} />
          </Routes>
          </GuardaDominio>
          </LimiteDeError>
        </div>
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
